import React, { useState, useEffect } from 'react';

const PagerDutyChannelForm = ({ channel, orgId, teamId, onSave, onCancel, apiCall }) => {
  const isTeamMode = !!teamId && !orgId;
  const [formData, setFormData] = useState({
    channelName: '',
    integrationKey: '',
    serviceName: '',
    isActive: true,
    isPrimary: false,
    autoResolveIncidents: true,
    organizationId: orgId,
    teamId: teamId
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (channel) {
      loadChannelData();
    }
  }, [channel]);

  const loadChannelData = async () => {
    try {
      const endpoint = isTeamMode
        ? `/api/alertchannelsapi/team/${teamId}/${channel.id}`
        : `/api/alertchannelsapi/${channel.id}`;
      const data = await apiCall(endpoint);
      setFormData({
        channelName: data.channelName || '',
        integrationKey: data.integrationKey || '',
        serviceName: data.serviceName || '',
        isActive: data.isActive !== false,
        isPrimary: data.isPrimary === true,
        autoResolveIncidents: data.autoResolveIncidents !== false,
        organizationId: orgId,
        teamId: teamId
      });
    } catch (err) {
      setError(err.message);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.integrationKey.trim()) {
      setError('Integration Key is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        channelId: channel?.id
      };

      if (channel) {
        // Update existing channel
        const updateEndpoint = isTeamMode
          ? `/api/alertchannelsapi/team/${teamId}/${channel.id}`
          : `/api/alertchannelsapi/${channel.id}`;
        await apiCall(updateEndpoint, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        // Create new channel
        const createEndpoint = isTeamMode
          ? '/api/alertchannelsapi/team/pagerduty'
          : '/api/alertchannelsapi/pagerduty';
        await apiCall(createEndpoint, {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0 }}>{channel ? 'Edit' : 'Create'} PagerDuty Channel</h3>
        <button
          type="button"
          onClick={onCancel}
          style={{
            border: 'none',
            background: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280'
          }}
        >
          ×
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          borderRadius: '6px'
        }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Channel Name *
          </label>
          <input
            type="text"
            value={formData.channelName}
            onChange={(e) => setFormData(prev => ({ ...prev, channelName: e.target.value }))}
            required
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            PagerDuty Integration Key *
          </label>
          <input
            type="text"
            value={formData.integrationKey}
            onChange={(e) => setFormData(prev => ({ ...prev, integrationKey: e.target.value }))}
            required
            placeholder="Your PagerDuty integration key"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
          <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            Find this in your PagerDuty service integration settings
          </p>
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
            Service Name (optional)
          </label>
          <input
            type="text"
            value={formData.serviceName}
            onChange={(e) => setFormData(prev => ({ ...prev, serviceName: e.target.value }))}
            placeholder="Production API"
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.autoResolveIncidents}
              onChange={(e) => setFormData(prev => ({ ...prev, autoResolveIncidents: e.target.checked }))}
            />
            Auto-Resolve Incidents When Alerts Resolve
          </label>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
            />
            Active
          </label>
        </div>

        <div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={formData.isPrimary}
              onChange={(e) => setFormData(prev => ({ ...prev, isPrimary: e.target.checked }))}
            />
            Set as Primary Channel
          </label>
        </div>

        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '10px 20px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              cursor: loading ? 'wait' : 'pointer',
              fontSize: '14px',
              fontWeight: 500,
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? 'Saving...' : (channel ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </form>
  );
};

export default PagerDutyChannelForm;

